import { customBaseDomainFromHost } from './customBaseDomain'
import { env } from './env'

type Target = 'auth' | 'api' | 'api-certificate'

const buildUrl = (
  protocol: string,
  host: string,
  port: string | undefined,
  path: string,
) => {
  const portSegment = port ? `:${port}` : ''
  return `${protocol}://${host}${portSegment}${path}`
}

const authUrlFromCurrentLocation = () => {
  const { host, protocol } = window.location
  // window.location.hostname はテストの部分モック ({ host } のみ等) で undefined になりうる。
  // 旧実装と同じく host から hostname を導出し、LOCKED ドメイン判定を安定させる
  // （production では host(port除く) と hostname は一致するため挙動は変わらない）。
  const hostname = (host || '').split(':')[0]
  const lockedAuthDomain = env.AUTH_DOMAIN
  const lockedAuthSuffix = `.${lockedAuthDomain}`

  if (
    hostname &&
    (hostname === lockedAuthDomain || hostname.endsWith(lockedAuthSuffix))
  ) {
    const subdomain = hostname.slice(0, -lockedAuthSuffix.length)
    const authHost = subdomain
      ? `${subdomain}.${lockedAuthDomain}`
      : lockedAuthDomain
    return buildUrl(env.AUTH_PROTCOL, authHost, env.AUTH_PORT, '/api/v1')
  }

  // それ以外（本番の custom domain `{tenant}.auth.{base}` 等）は auth API が同一オリジン。
  // path 先頭セグメントを tenant 扱いする旧実装は廃止（/login 等のページ名を誤認するため）。
  return `${protocol}//${host}/api/v1`
}

// マイページ系 API のオリジン。
// custom domain（partner）では API も partner の base domain（apex）に寄せて same-site にする。
// auth-client は単一ビルドを全ドメインで配信するため、app / extension のようなビルド時
// per-partner ドメイン指定ができない。代わりに 'auth'（同一オリジン）/ 'api-certificate'
// （certificate.{base}）と同じくランタイムで現在ホストから導出する。
// これにより partner ドメイン上のマイページ API が `{tenant}.auth.{base}` → `{base}` への
// same-site 呼び出しになり、locked.jp への cross-site（third-party cookie ブロック）を避けられる。
// LOCKED ドメイン配下では customBaseDomainFromHost が null を返すため従来どおり env.API_DOMAIN。
// 前提: partner は locked-api を base apex（例 acme.jp）で受ける（app/extension の API_BASE_DOMAIN と同規約）。
const apiUrlFromCurrentLocation = () => {
  if (typeof window !== 'undefined') {
    const base = customBaseDomainFromHost(window.location.hostname)
    if (base) {
      // api-certificate と同じく現在のポートを維持する（dev/staging の非標準ポート対策。本番は port 無しで不変）。
      const portSegment = window.location.port ? `:${window.location.port}` : ''
      return `${window.location.protocol}//${base}${portSegment}/api/v1`
    }
  }
  return buildUrl(env.API_PROTCOL, env.API_DOMAIN, env.API_PORT, '/api/v1')
}

// 証明書(mTLS) API のオリジン。custom domain では certificate.{base}（§5.3）。
const certificateUrlFromCurrentLocation = () => {
  if (typeof window !== 'undefined') {
    // custom auth host `{tenant}.auth.{base}` の base domain 解決は cookie domain と同じ
    // customBaseDomainFromHost を使う（重複定義による drift を防ぐ。cursor #366 Low 指摘）。
    const base = customBaseDomainFromHost(window.location.hostname)
    if (base) {
      // auth URL（authUrlFromCurrentLocation の host）と同じく現在のポートを維持する。
      // 落とすと dev/staging の非標準ポートで mTLS チェックが別オリジンに飛ぶ（本番は port 無しで不変）。
      const portSegment = window.location.port ? `:${window.location.port}` : ''
      return `${window.location.protocol}//${env.CERTIFICATE_API_SUBDOMAIN}${base}${portSegment}/api/check_certificate`
    }
  }
  return buildUrl(
    env.API_PROTCOL,
    `${env.CERTIFICATE_API_SUBDOMAIN}${env.API_DOMAIN}`,
    env.CERTIFICATE_API_PORT,
    '/api/check_certificate',
  )
}

export const fullUrl = (target: Target = 'auth') => {
  if (target === 'api') {
    return apiUrlFromCurrentLocation()
  } else if (target === 'api-certificate') {
    return certificateUrlFromCurrentLocation()
  }

  if (typeof window === 'undefined' && target === 'auth') {
    return buildUrl(env.AUTH_PROTCOL, env.AUTH_DOMAIN, env.AUTH_PORT, '/api/v1')
  } else if (target === 'auth') {
    return authUrlFromCurrentLocation()
  }
}
